import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React, { useEffect, useState } from "react";
import { toast } from "sonner";

import * as sync from "@/api/stock_sync";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toastError } from "@/lib/api-errors";
import { LoadState } from "@/lib/scan-ui/screen";
import { STOCK_SYNC_STATUS_KEY } from "@/pages/settings/cards/StockSyncCard";

export const SERVICE_CONNECTION_KEY = ["stock-sync", "service-connection"] as const;

/**
 * Служебное подключение 1С для синхронизации остатков — только у инсталляции,
 * где оно хранится в портале (`source === "db"`); у остальных оно берётся из
 * Infisical, и карточки нет. Источник — из ответа самой ручки, не угадываем.
 *
 * ⚠️ Пароль отправляется, ТОЛЬКО если его ввели: пустое поле — «не менять».
 */
export function ServiceOneCCard(): React.ReactElement | null {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: SERVICE_CONNECTION_KEY, queryFn: sync.serviceConnection });
  const [baseUrl, setBaseUrl] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [result, setResult] = useState<sync.ServiceConnectionTest | null>(null);

  useEffect(() => {
    if (!q.data) return;
    setBaseUrl(q.data.base_url ?? "");
    setUsername(q.data.username ?? "");
  }, [q.data]);

  const save = useMutation({
    mutationFn: sync.saveServiceConnection,
    onSuccess: (data) => {
      qc.setQueryData(SERVICE_CONNECTION_KEY, data);
      void qc.invalidateQueries({ queryKey: STOCK_SYNC_STATUS_KEY });
      setPassword("");
      setResult(null);
      toast.success("Сохранено");
    },
    onError: toastError("Не удалось сохранить"),
  });

  const test = useMutation({
    mutationFn: sync.testServiceConnection,
    onSuccess: setResult,
    onError: toastError("Не удалось выполнить проверку"),
  });

  if (q.isLoading) return null;
  if (q.error || !q.data) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Служебное подключение 1С</CardTitle>
        </CardHeader>
        <CardContent>
          <LoadState isLoading={false} error={q.error} onRetry={() => void q.refetch()} />
        </CardContent>
      </Card>
    );
  }
  if (q.data.source !== "db") return null;

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const body: sync.ServiceConnectionUpdate = { base_url: baseUrl, username };
    if (password) body.password = password;
    save.mutate(body);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Служебное подключение 1С</CardTitle>
      </CardHeader>
      <CardContent>
        <p className="mb-3 text-sm text-muted-foreground">
          Учётная запись, под которой синхронизация остатков читает 1С. Адрес базы
          должен совпадать с базой, в которой ведутся поставки ФБС.
        </p>
        <form onSubmit={onSubmit} className="space-y-3">
          <div className="space-y-2">
            <Label htmlFor="svc1c_url">Адрес базы 1С</Label>
            <Input
              id="svc1c_url"
              placeholder="http://сервер/база"
              value={baseUrl}
              onChange={(e) => setBaseUrl(e.target.value)}
            />
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="svc1c_user">Логин</Label>
              <Input
                id="svc1c_user"
                autoComplete="off"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="svc1c_pwd">Пароль</Label>
              <Input
                id="svc1c_pwd"
                type="password"
                autoComplete="new-password"
                placeholder={
                  q.data.password_set ? "задан — оставьте пустым, чтобы не менять" : ""
                }
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button type="submit" disabled={save.isPending}>
              Сохранить
            </Button>
            <Button
              type="button"
              variant="outline"
              disabled={test.isPending}
              onClick={() => test.mutate()}
            >
              Проверить подключение
            </Button>
          </div>
          {result && (
            <p
              role="status"
              className={result.ok ? "text-sm text-green-700" : "text-sm text-destructive"}
            >
              {result.detail}
            </p>
          )}
        </form>
      </CardContent>
    </Card>
  );
}
